/**
 * `useOrgMaterialRequests` — live read of every material request in
 * the org, with optional status filter.
 *
 * Sibling to `useMaterialRequests(orgId, projectId, status?)` — same
 * collection, same shape, but no `projectId` filter. Drives the
 * Dashboard's "pending approvals" tile and the org-wide audit
 * surfaces where you need to see what's piling up across projects.
 *
 * Firestore rules already scope reads to org members (see
 * `firestore.rules` → `match /materialRequests/{requestId}`), and
 * non-admin roles only get back docs on projects they can see (via
 * the `canSeeProject` rule in the same block). So we don't need to
 * layer client-side role filtering on top — the query just returns
 * what the caller is allowed.
 */
import {
  collection,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';

import { db } from '@/lib/firebase';
import type {
  MaterialRequest,
  MaterialRequestStatus,
} from '@/lib/materialRequests';

export type UseOrgMaterialRequestsResult = {
  data: MaterialRequest[];
  loading: boolean;
};

export function useOrgMaterialRequests(
  orgId: string | null | undefined,
  status?: MaterialRequestStatus | null,
): UseOrgMaterialRequestsResult {
  const [all, setAll] = useState<MaterialRequest[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orgId) {
      setAll([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    return onSnapshot(
      query(
        collection(db, 'materialRequests'),
        where('orgId', '==', orgId),
      ),
      (snap) => {
        const rows: MaterialRequest[] = snap.docs.map((d) => ({
          id: d.id,
          ...(d.data() as Omit<MaterialRequest, 'id'>),
        }));
        rows.sort((a, b) => {
          const am = a.createdAt?.toMillis?.() ?? 0;
          const bm = b.createdAt?.toMillis?.() ?? 0;
          return bm - am;
        });
        setAll(rows);
        setLoading(false);
      },
      (err) => {
        console.warn('[useOrgMaterialRequests] snapshot error:', err);
        setLoading(false);
      },
    );
  }, [orgId]);

  const data = useMemo(() => {
    if (!status) return all;
    return all.filter((r) => r.status === status);
  }, [all, status]);

  return { data, loading };
}
